<!--
 * @Description: 个体发票开票记录（16种按钮组合），只有运营基地有全部操作权限，非基地没有上传/补充/修改操作
 * @Date: 2026-06-04
-->
<template>
  <XrkTable
    ref="xrkTableRef"
    :getListAjax="getListAjax"
    :searchConfig="searchConfig"
    :tableConfig="tableConfig"
  />
</template>
<script lang="tsx" setup>
import { ref } from 'vue';
import {
  XrkTable,
  XrkTableProps,
  useTableColumn,
  BaseButtonMore,
  useCreateModal,
  BaseButton,
  XrkMessage,
  XrkMessageBox
} from 'xrk-components';
import { formatter } from '@/utils';
import {
  queryInvoiceList,
  invoiceExport,
  downloadTaxForId,
  jlBatchDownload,
  withdrawInvoice
} from '@V/invoice/src/data-source/apis';
import { taxPatternMapping } from '@constants/project';
import {
  invoiceOrderNoSearch,
  enterpriseNameSearch,
  supplierNameSearch,
  timeRangeSearch,
  invoiceTypeSearch,
  blueInvoiceStatusSearch,
  redInvoiceStatusSearch,
  updateTimeSearch
} from '@V/invoice/src/fields/searchs';
import {
  invoiceOrderNoCol,
  enterpriseNameCol,
  supplierNameCol,
  orderTypeCol,
  invoiceTypeCol,
  blueInvoiceStatusCol,
  redInvoiceStatusCol,
  applyTimeCol,
  updateTimeCol,
  invoiceAmountCol
} from '@V/invoice/src/fields/columns';
import InvoiceDetailGtDialog from '../invoice-detail-dialog/invoice-detail-gt-dialog.vue';
import InvoiceApplyIndividualDialog from '../invoice-apply-individual-dialog/invoice-apply-individual-dialog.vue';
import { useBatchDownload } from '@V/invoice/src/hooks';
import { IS_PLATFORM_TYPE_1 } from '@/config';
import type { GtButtonSource } from '@V/invoice/src/types';
import appStore from '@/store';
import { storeToRefs } from 'pinia';

const props = withDefaults(
  defineProps<{
    invoiceTabType?: number;
  }>(),
  {
    invoiceTabType: 1
  }
);

const { info: userInfo } = storeToRefs(appStore.useUserStoreSetup);

const xrkTableRef = ref();
const { selectionField, download } = useBatchDownload({
  xrkTableRef,
  ajax: jlBatchDownload,
  options: {
    passStatus: [2, 6, 12]
  }
});

const getListAjax = async params => {
  return queryInvoiceList({
    ...params,
    type: props.invoiceTabType,
    invoiceBusinessTypeList: [7]
  });
};

const searchConfig = ref<XrkTableProps['searchConfig']>({
  searchColumns: [
    invoiceOrderNoSearch(),
    enterpriseNameSearch(),
    supplierNameSearch(),
    timeRangeSearch(),
    invoiceTypeSearch(),
    blueInvoiceStatusSearch(),
    redInvoiceStatusSearch(),
    updateTimeSearch()
  ].filter(Boolean),
  searchBtn: {
    show: true,
    text: '搜索'
  },
  resetBtn: {
    show: true,
    text: '重置'
  },
  otherComponents: [
    {
      render() {
        return (
          <>
            <BaseButton
              type="primary2"
              click={async () => {
                await download();
              }}
            >
              批量导出
            </BaseButton>
          </>
        );
      }
    }
  ]
});

const tableConfig = ref<XrkTableProps['tableConfig']>({
  tableColumns: [
    selectionField(),
    useTableColumn({
      type: 'index',
      label: '序号',
      width: '80px',
      fixed: 'left'
    }),
    invoiceOrderNoCol(),
    enterpriseNameCol(),
    supplierNameCol(),
    orderTypeCol(),
    IS_PLATFORM_TYPE_1 &&
      useTableColumn({
        label: '交易结构',
        prop: 'professionType',
        formatter: ({ cellValue }) =>
          formatter.defaultFormat(
            cellValue,
            () => taxPatternMapping[cellValue]?.label
          )
      }),
    applyTimeCol(),
    invoiceAmountCol(),
    useTableColumn({
      label: '票面金额(元)',
      prop: 'sumAmount',
      formatter: ({ cellValue }) => formatter.fen2yuan(cellValue)
    }),
    invoiceTypeCol(),
    useTableColumn({
      label: '开票类目',
      prop: 'itemName',
      formatter: ({ cellValue }) => formatter.defaultFormat(cellValue)
    }),
    blueInvoiceStatusCol(),
    redInvoiceStatusCol(),
    updateTimeCol(),

    useTableColumn({
      label: '操作',
      prop: 'handlerCol',
      fixed: 'right',
      width: '200px',
      render: ({ row }) => {
        const BS = row.blueInvoiceStatus;
        const RS = row.redInvoiceStatus;

        const openGtDetail = (buttonSource: GtButtonSource) => {
          useCreateModal(InvoiceDetailGtDialog, {
            id: row.id,
            scene: 1,
            buttonSource,
            onSuccess() {
              xrkTableRef.value?.initCurrentList?.();
            }
          });
        };

        const openReapply = () => {
          useCreateModal(InvoiceApplyIndividualDialog, {
            id: row.id,
            onSuccess() {
              xrkTableRef.value?.initCurrentList?.();
            }
          });
        };

        const btns = [
          // 撤销申请：BS === -2
          BS === -2 && {
            content: '撤销申请',
            click: async () => {
              try {
                await XrkMessageBox.confirm(
                  '是否确认撤回该开票申请？',
                  '提示',
                  {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning'
                  }
                );
                await withdrawInvoice({ invoiceApplyId: row.id });
                XrkMessage.success('撤回成功');
                xrkTableRef.value?.initCurrentList?.();
              } catch {
                // 用户取消
              }
            }
          },
          // 重新申请：BS === 13
          BS === 13 && {
            content: '重新申请',
            click: () => openReapply()
          },
          // 详情：BS∈[2,12]（已开票/部分开票）
          [2, 12].includes(BS) && {
            content: '详情',
            click: () => openGtDetail(11)
          },
          // 上传蓝票：userInfo.value.isBaseAsp === 1 && [0,11].includes(BS) && (!RS || [7,9].includes(RS))
          userInfo.value.isBaseAsp === 1 &&
            [0, 11].includes(BS) &&
            (!RS || [7, 9].includes(RS)) && {
              content: '上传蓝票',
              click: () => openGtDetail(1)
            },
          // 审核蓝票：BS === 1
          BS === 1 && {
            content: '审核蓝票',
            click: () => openGtDetail(6)
          },
          // 修改上传(蓝)：userInfo.value.isBaseAsp === 1 && BS === 3
          userInfo.value.isBaseAsp === 1 &&
            BS === 3 && {
              content: '修改上传',
              click: () => openGtDetail(3)
            },
          // 补充蓝票：userInfo.value.isBaseAsp === 1 && BS === 12 && (!RS || [6,7,8,9,10].includes(RS))
          userInfo.value.isBaseAsp === 1 &&
            BS === 12 &&
            (!RS || [6, 7, 8, 9, 10].includes(RS)) && {
              content: '补充蓝票',
              click: () => openGtDetail(2)
            },
          // 申请冲红：[2,12].includes(BS) && !RS
          [2, 12].includes(BS) &&
            !RS && {
              content: '申请冲红',
              click: () => openGtDetail(8)
            },
          // 上传红票：userInfo.value.isBaseAsp === 1 && [2,12].includes(BS) && RS === 4
          userInfo.value.isBaseAsp === 1 &&
            [2, 12].includes(BS) &&
            RS === 4 && {
              content: '上传红票',
              click: () => openGtDetail(4)
            },
          // 审核红票：RS === 5
          RS === 5 && {
            content: '审核红票',
            click: () => openGtDetail(7)
          },
          // 修改上传红票：userInfo.value.isBaseAsp === 1 && RS === 7
          userInfo.value.isBaseAsp === 1 &&
            [2, 12].includes(BS) &&
            RS === 7 && {
              content: '修改上传红票',
              click: () => openGtDetail(5)
            },
          // 补充红票：userInfo.value.isBaseAsp === 1 && [2,12].includes(BS) && RS === 10
          userInfo.value.isBaseAsp === 1 &&
            [2, 12].includes(BS) &&
            RS === 10 && {
              content: '补充红票',
              click: () => openGtDetail(15)
            },
          // 继续冲红：[2,12].includes(BS) && RS === 10
          [2, 12].includes(BS) &&
            RS === 10 && {
              content: '继续冲红',
              click: () => openGtDetail(10)
            },
          // 重新申请冲红：RS === 9
          RS === 9 && {
            content: '重新申请冲红',
            click: () => openGtDetail(9)
          },
          // 下载开票信息：始终显示
          {
            content: '下载开票信息',
            click: async () => {
              await invoiceExport({ invoiceId: row.id });
            }
          },
          // 下载发票：[2,12].includes(BS) || [6,10].includes(RS)
          ([2, 12].includes(BS) || [6, 10].includes(RS)) && {
            content: '下载发票',
            click: async () => {
              await downloadTaxForId({ invoiceId: row.id });
            }
          }
        ].filter(Boolean);

        return (
          <BaseButtonMore
            key={Date.now()}
            maxCount={3}
            style={{ 'justify-content': 'right' }}
            btns={btns}
          ></BaseButtonMore>
        );
      }
    })
  ].filter(Boolean)
});
</script>
